import React, { useState, useRef, useContext, useEffect } from 'react';
import EditableField from '../../components/shared/editable-input';
import { RxCross2 } from 'react-icons/rx';
import ModalHeading from '../../components/shared/modal-heading';
import { AccountsContext } from '../../components/context/account-context';
import { prependDefault } from '../../utils/structure-options-utils';
import { uploadMedia } from '../../services/upload-service';
import { getExpenseMedia, updateExpense } from '../../services/expenses-service';
import MultipleAttachmentInput from '../../components/shared/multiple-attachemnt';
import Loader from '../../components/shared/loader';

const EditModal = ({
    selectedRow,
    setSelectedRow,
    setIsEditModalOpen,
    setData,
    fetchExpenses,
    isVehicleTab = false,
    spendors,
    vendors,
    vehicles,
    categories,
    paymentMethods,
    jobsOptions,
    showVehicle = true,
    globalDataloading
}) => {
    const { accountId,attributes } = useContext(AccountsContext);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [uploadStatus, setUploadStatus] = useState({ message: '', type: '' });
    const [updateStatus, setUpdateStatus] = useState({ message: '', type: '' });
    const [loadingFields, setLoadingFields] = useState({});
    const [isUploading, setIsUploading] = useState(false);
    const [fetchingMedia, setFetchingMedia] = useState(false);
    const [error, setError] = useState(null);
    const fileInputRef = useRef(null);

    // Fetch expense media
    const fetchExpenseMedia = async () => {
        try {
            setFetchingMedia(true);
            const res = await getExpenseMedia(accountId, selectedRow.expenseId);
            console.log("Media : ", res)
            if (res.success) {
                const mediaFiles50 = res.data?.data || [];
                setUploadedFiles(mediaFiles50.map(file => ({
                    mediaId: file.mediaId,
                    fileType: file.fileType,
                    fileName: file.fileName,
                    signedUrl: file.signedUrl,
                    signedThumbUrl: file.signedThumbUrl,
                    imagePreview: file.fileType?.startsWith('image/') ? file.signedThumbUrl || file.signedUrl : null
                })));
                setError(null);
            } else {
                setError(res.data?.message || 'Failed to fetch media');
            }
        } catch (error) {
            console.error('Error fetching expense media:', error);
            setError('An error occurred while fetching media');
        } finally {
            setFetchingMedia(false);
        }
    };

    useEffect(() => {
        fetchExpenseMedia();
    }, [accountId, selectedRow.expenseId]);

    // Clear status messages after 2 seconds
    useEffect(() => {
        let timer;
        if (uploadStatus.message && uploadStatus.type !== 'info') {
            timer = setTimeout(() => {
                setUploadStatus({ message: '', type: '' });
            }, 2000);
        }
        return () => clearTimeout(timer);
    }, [uploadStatus]);

    useEffect(() => {
        let timer;
        if (updateStatus.message && updateStatus.type !== 'info') {
            timer = setTimeout(() => {
                setUpdateStatus({ message: '', type: '' });
            }, 2000);
        }
        return () => clearTimeout(timer);
    }, [updateStatus]);

    // Handle multiple file uploads
    const handleImageUpload = async (files) => {
        if (files.length === 0) return;

        setIsUploading(true);
        setUploadStatus({ message: '', type: '' });

        try {
            const uploadPromises = Array.from(files).map((file) =>
                uploadMedia(accountId, file, 'expense', selectedRow.expenseId)
            );
            const uploadResponses = await Promise.all(uploadPromises);

            const successfulUploads = uploadResponses.filter(res => res.success);
            const failedUploads = uploadResponses.filter(res => !res.success);

            if (failedUploads.length > 0) {
                setUploadStatus({
                    message: `${failedUploads.length} file(s) failed to upload`,
                    type: 'error',
                });
            }

            if (successfulUploads.length > 0) {
                const newFiles = successfulUploads.map(res => ({
                    mediaId: res.data.data.mediaId,
                    fileType: res.data.data.fileType,
                    fileName: res.data.data.fileName,
                    signedUrl: res.data.data.signedUrl,
                    signedThumbUrl: res.data.data.signedThumbUrl,
                    imagePreview: res.data.data.fileType?.startsWith('image/') ? res.data.data.signedThumbUrl || res.data.data.signedUrl : null
                }));

                setUploadedFiles(prev => [...prev, ...newFiles]);
                setUploadStatus({
                    message: `${successfulUploads.length} file(s) uploaded successfully!`,
                    type: 'success',
                });

                // Update the selected row and data state
                const updatedRow = {
                    ...selectedRow,
                    expense: {
                        ...selectedRow.expense,
                        media: [...(selectedRow.expense?.media || []), ...newFiles],
                    },
                };
                setSelectedRow(updatedRow);
                setData((prevData) =>
                    prevData.map((row) => (row.expenseId === selectedRow.expenseId ? updatedRow : row))
                );
            }
        } catch (error) {
            console.error('Error uploading media:', error);
            setUploadStatus({
                message: 'Failed to upload files',
                type: 'error',
            });
        } finally {
            setIsUploading(false);
        }
    };

    // Handle file removal
    const handleRemoveFile = async (fileToRemove) => {
        try {
            setUploadedFiles(prev => prev.filter(file => file.mediaId !== fileToRemove.mediaId));
            setUploadStatus({
                message: 'File removed successfully',
                type: 'success',
            });

            // Update the selected row and data state
            const updatedRow = {
                ...selectedRow,
                expense: {
                    ...selectedRow.expense,
                    media: selectedRow.expense?.media?.filter(file => file.mediaId !== fileToRemove.mediaId) || [],
                },
            };
            setSelectedRow(updatedRow);
            setData((prevData) =>
                prevData.map((row) => (row.expenseId === selectedRow.expenseId ? updatedRow : row))
            );
        } catch (error) {
            console.error('Error removing file:', error);
            setUploadedFiles(prev => [...prev, fileToRemove]);
            setUploadStatus({
                message: 'Failed to remove file',
                type: 'error',
            });
        }
    };

    const handleFieldUpdate = async (value, fieldName) => {
        setLoadingFields((prev) => ({ ...prev, [fieldName]: true }));
        setUpdateStatus({ message: '', type: '' });

        const fieldMap = {
            time: 'expenseTime',
            vendor: 'vendorId',
            vehicle: 'vehicleId',
            job: 'jobId',
            spender: 'spentBy',
            category: 'category',
            paymentMethod: 'paymentMethod',
            shared: 'shared',
            status: 'status',
            note: 'note',
        };

        const payload = {};
        if (fieldName === 'time') {
            payload[fieldMap[fieldName]] = new Date(value).toISOString();
        }

        else if (fieldName === 'shared') {
            payload[fieldMap[fieldName]] = value === 'Yes';
        } else {
            payload[fieldMap[fieldName]] = value;
        }

        try {
            const response = await updateExpense(accountId, selectedRow.expenseId, payload);
            if (response.success) {
                const updatedRow = { ...selectedRow };
                switch (fieldName) {
                    case 'time':
                        updatedRow.expenseTime = payload.expenseTime;
                        break;
                    case 'vendor':
                        updatedRow.vendor = {
                            vendorId: value,
                            name: vendorOptions.find((opt) => opt.value === value)?.label || '',
                        };
                        break;
                    case 'vehicle':
                        updatedRow.vehicle = {
                            vehicleId: value,
                            name: vehicleOptions.find((opt) => opt.value === value)?.label || '',
                        };
                        break;
                    case 'job':
                        updatedRow.jobId = value;
                        break;
                    case 'spender':
                        updatedRow.spender = {
                            userId: value,
                            name: spenderOptions.find((opt) => opt.value === value)?.label || '',
                        };
                        break;
                    case 'category':
                        updatedRow.category = value;
                        break;
                    case 'paymentMethod':
                        updatedRow.paymentMethod = value;
                        break;
                    default:
                        updatedRow[fieldName] = value;
                }
                setSelectedRow(updatedRow);
                setData((prevData) =>
                    prevData.map((row) => (row.expenseId === selectedRow.expenseId ? updatedRow : row))
                );
                setUpdateStatus({
                    message: `${labelMap[fieldName] || fieldName} updated successfully!`,
                    type: 'success',
                });
                isVehicleTab && fetchExpenses?.('Expenses');
            } else {
                setUpdateStatus({
                    message: response.data.message || `Failed to update ${labelMap[fieldName] || fieldName}`,
                    type: 'error',
                });
                // throw new Error('API update failed');
            }
        } catch (error) {
            setUpdateStatus({
                message: error.message || `Failed to update ${labelMap[fieldName] || fieldName}`,
                type: 'error',
            });
            throw error;
        } finally {
            setLoadingFields((prev) => ({ ...prev, [fieldName]: false }));
        }
    };

    const labelMap = {
        time: 'Time',
        vendor: 'Vendor',
        vehicle: 'Vehicle',
        job: 'Job',
        spender: 'Spender',
        category: 'Category',
        paymentMethod: 'Payment Method',
        baseAmount: 'Amount',
        taxOption: 'Tax Option',
        taxPercentage: 'Tax Percentage',
        shared: 'Shared',
        status: 'Status',
        note: 'Note',
    };


    const vendorOptions = prependDefault(
        vendors.map((v) => ({ value: v.vendorId, label: v.label })),
        'Vendor'
    );
    const vehicleOptions = vehicles && prependDefault(
        vehicles.map((v) => ({ value: v.vehicleId, label: v.label })),
        'Vehicle'
    );
    const spenderOptions = prependDefault(
        spendors.map((s) => ({ value: s.userId, label: s.label })),
        'Spender'
    );
    const categoryOptions = prependDefault(
        categories.map((c) => ({ value: c.slug, label: c.label })),
        'Category'
    );
    const paymentMethodOptions = prependDefault(
        paymentMethods.map((p) => ({ value: p.slug, label: p.label })),
        'Payment Method'
    );
    const jobOptions = prependDefault(
        jobsOptions.map((j) => ({ value: j.jobId, label: j.label })),
        'Jobs'
    );
    const sharedOptions = [
        { value: 'Yes', label: 'Yes' },
        { value: 'No', label: 'No' },
    ];

    return (
        <div className="space-y-3 px-4 pt-3 relative">
            <ModalHeading className="text-left mb-3">Expense Detail Information</ModalHeading>

            <button
                className="hover:scale-105 absolute -top-3 -right-2 bg-white border p-2 rounded-full"
                onClick={() => setIsEditModalOpen(false)}
            >
                <RxCross2 className="text-xl" />
            </button>

            {(uploadStatus.message || updateStatus.message || error) && (
                <div
                    className={`px-4 py-2 text-sm ${(uploadStatus.type || updateStatus.type) === 'success'
                        ? 'text-green-600 bg-green-100'
                        : 'text-red-600 bg-red-100'
                        } rounded-md mx-4`}
                >
                    {uploadStatus.message || updateStatus.message || error}
                </div>
            )}

            {(fetchingMedia || globalDataloading) ? (
                <Loader />
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <EditableField
                        label="Time"
                        name="time"
                        value={selectedRow?.expenseTime || ''}
                        onChange={handleFieldUpdate}
                        type="datetime-local"
                        isLoading={loadingFields.time}
                    />

                    <EditableField
                        label="Category"
                        name="category"
                        type="select"
                        value={selectedRow?.category || ''}
                        options={categoryOptions}
                        onChange={handleFieldUpdate}
                        isLoading={loadingFields.category}
                    />

                    <EditableField
                        label="Vendor"
                        name="vendor"
                        type="select"
                        options={vendorOptions}
                        value={selectedRow?.vendor?.vendorId || ''}
                        onChange={handleFieldUpdate}
                        isLoading={loadingFields.vendor}
                    />

                    {
                        showVehicle &&
                        <EditableField
                            label="Vehicle"
                            name="vehicle"
                            type="select"
                            value={selectedRow?.vehicle?.vehicleId || ''}
                            options={vehicleOptions}
                            onChange={handleFieldUpdate}
                            isEditable={selectedRow?.vehicle?.vehicleId ? (!isVehicleTab) : false}
                            isLoading={loadingFields.vehicle}
                        />
                    }


                    <EditableField
                        label="Job"
                        name="job"
                        type="select"
                        value={selectedRow?.jobId || ''}
                        options={jobOptions}
                        onChange={handleFieldUpdate}
                        isLoading={loadingFields.job}
                        isEditable={!showVehicle ? false : selectedRow?.jobId ? true : false}
                    />

                    <EditableField
                        label="Spender"
                        name="spender"
                        type="select"
                        options={spenderOptions}
                        value={selectedRow?.spender?.userId || ''}
                        onChange={handleFieldUpdate}
                        isLoading={loadingFields.spender}
                    />

                    <EditableField
                        label="Amount"
                        name="amount"
                        value={selectedRow?.amount || ''}
                        onChange={handleFieldUpdate}
                        type="number"
                        isEditable={false}
                        isLoading={loadingFields.baseAmount}
                        unit={attributes?.currency}
                    />

                    <EditableField
                        label="Payment Method"
                        name="paymentMethod"
                        type="select"
                        value={selectedRow?.paymentMethod || ''}
                        options={paymentMethodOptions}
                        onChange={handleFieldUpdate}
                        isLoading={loadingFields.paymentMethod}
                    />

                    <EditableField
                        label="Shared"
                        name="shared"
                        type="select"
                        options={sharedOptions}
                        value={selectedRow?.shared ? 'Yes' : 'No'}
                        onChange={handleFieldUpdate}
                        isLoading={loadingFields.shared}
                    />

                    <EditableField
                        label="Status"
                        name="status"
                        type="select"
                        value={selectedRow?.status || 'submitted'}
                        onChange={handleFieldUpdate}
                        isEditable={false}
                        isLoading={loadingFields.status}
                    />

                    <EditableField
                        label="Note"
                        name="note"
                        type="textarea"
                        rows={2}
                        value={selectedRow?.note || ''}
                        onChange={handleFieldUpdate}
                        isLoading={loadingFields.note}
                    />

                    <MultipleAttachmentInput
                        handleFileUpload={handleImageUpload}
                        uploadedFiles={uploadedFiles}
                        handleRemoveFile={handleRemoveFile}
                        isUploading={isUploading}
                        accountId={accountId}
                        fileInputRef={fileInputRef}
                    />
                </div>
            )}
        </div>
    );
};

export default EditModal;